import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarDays, Clock, Loader2, CheckCircle2, ArrowRight, ChevronLeft, ChevronRight, Globe, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { TIMEZONES, detectTimezone, formatInTz } from "../../lib/timezones";

const inputCls =
  "w-full rounded-xl border border-[#1e3d3a]/15 bg-white px-4 py-3 text-sm text-[#1e3d3a] placeholder:text-[#1e3d3a]/35 focus:border-[#c9a961] focus:outline-none focus:ring-2 focus:ring-[#c9a961]/25 transition-colors duration-300";

const dayLabel = (iso) => {
  const d = new Date(iso);
  return {
    weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
    month: d.toLocaleDateString(undefined, { month: "short" }),
    day: d.getDate(),
    key: d.toDateString(),
  };
};
const timeLabel = (iso, tz) => formatInTz(iso, tz, { hour: "numeric", minute: "2-digit", hour12: true });
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function BookingCalendar() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [tz, setTz] = useState(() => {
    try {
      const saved = typeof window !== "undefined" ? window.localStorage.getItem("elevare_tz") : null;
      return saved || detectTimezone();
    } catch {
      return detectTimezone();
    }
  });
  useEffect(() => {
    try { window.localStorage.setItem("elevare_tz", tz); } catch { /* ignore */ }
  }, [tz]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", goal: "" });
  const [submitting, setSubmitting] = useState(false);
  const [showFee, setShowFee] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const isAdmin = typeof window !== "undefined" &&
    (!!localStorage.getItem("elevare_admin_secret") || !!localStorage.getItem("elevare_admin_user"));
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const load = () => {
    setLoading(true);
    api.get("/bookings/slots")
      .then(({ data }) => setSlots(data.slots || []))
      .catch(() => toast.error("Could not load times."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const daysMap = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const info = dayLabel(s.slot_iso);
      if (!map.has(info.key)) map.set(info.key, { ...info, slots: [] });
      map.get(info.key).slots.push(s);
    });
    return map;
  }, [slots]);

  // Bounds for prev/next: don't allow navigating past the last month that has slots
  const monthBounds = useMemo(() => {
    if (!slots.length) return null;
    const firstIso = slots[0].slot_iso;
    const lastIso = slots[slots.length - 1].slot_iso;
    const first = new Date(firstIso);
    const last = new Date(lastIso);
    return {
      min: new Date(first.getFullYear(), first.getMonth(), 1),
      max: new Date(last.getFullYear(), last.getMonth(), 1),
    };
  }, [slots]);

  // 42-cell grid (6 rows × 7 cols) starting on Sunday on/before the 1st
  const monthCells = useMemo(() => {
    const first = new Date(viewMonth);
    const start = new Date(first);
    start.setDate(1 - first.getDay());
    const cells = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      cells.push({
        date: d,
        key: d.toDateString(),
        inMonth: d.getMonth() === viewMonth.getMonth(),
        dayNum: d.getDate(),
      });
    }
    return cells;
  }, [viewMonth]);

  // Auto-select the first day with open slots when data lands
  useEffect(() => {
    if (!selectedDayKey && daysMap.size) {
      const firstKey = Array.from(daysMap.keys())[0];
      setSelectedDayKey(firstKey);
      // Jump the calendar view to the month of the first bookable day if it's different
      const first = daysMap.get(firstKey);
      if (first) {
        const target = new Date(first.date || firstKey);
        if (target.getMonth() !== viewMonth.getMonth() || target.getFullYear() !== viewMonth.getFullYear()) {
          setViewMonth(new Date(target.getFullYear(), target.getMonth(), 1));
        }
      }
    }
  }, [daysMap, selectedDayKey, viewMonth]);

  const activeDay = selectedDayKey ? daysMap.get(selectedDayKey) : null;

  const shiftMonth = (delta) => {
    setViewMonth((m) => {
      const next = new Date(m.getFullYear(), m.getMonth() + delta, 1);
      if (monthBounds) {
        if (next < monthBounds.min) return monthBounds.min;
        if (next > monthBounds.max) return monthBounds.max;
      }
      return next;
    });
  };

  const canPrev = !monthBounds || viewMonth > monthBounds.min;
  const canNext = !monthBounds || viewMonth < monthBounds.max;
  const monthTitle = viewMonth.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedSlot) { toast.error("Pick a time first."); return; }
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required."); return; }
    setShowFee(true);
  };

  const payWithFee = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/assessment-checkout", {
        slot_iso: selectedSlot.slot_iso,
        tz,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not start checkout. Please try again.";
      toast.error(msg);
      if (err?.response?.status === 409) { setShowFee(false); load(); }
      setSubmitting(false);
    }
  };

  const simulate = async () => {
    if (!selectedSlot) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/simulate-assessment", {
        slot_iso: selectedSlot.slot_iso,
        tz,
        ...form,
        origin_url: window.location.origin,
      });
      setShowFee(false);
      setConfirmation(data);
      toast.success("Simulated booking confirmed — no charge made.");
      load();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Simulation failed.";
      toast.error(msg);
      if (err?.response?.status === 409) { setShowFee(false); load(); }
    } finally {
      setSubmitting(false);
    }
  };

  if (confirmation) {
    return (
      <motion.div
        data-testid="booking-confirmed"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-3xl border border-[#c9a961]/40 bg-[#f4f1e8] p-8 lg:p-10 text-center"
      >
        <CheckCircle2 className="h-10 w-10 text-[#6b7c3a] mx-auto" />
        <h4 className="mt-4 font-head text-2xl text-[#1e3d3a]">You&apos;re on the calendar</h4>
        <p className="mt-3 text-sm text-[#1e3d3a]/70">
          <strong className="text-[#1e3d3a]">{confirmation.pretty}</strong><br />
          A confirmation just landed in your inbox. Kalib or John will reach out at this time.
        </p>
        <p className="mt-4 text-[11px] uppercase tracking-[0.2em] text-[#1e3d3a]/45">
          Ref · {confirmation.id?.slice(0, 8)}
        </p>
      </motion.div>
    );
  }

  return (
    <div data-testid="booking-calendar" className="rounded-3xl border border-[#1e3d3a]/15 bg-white p-6 lg:p-8">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#c9a961] font-medium">
        <CalendarDays className="h-3.5 w-3.5" /> Self-book · 30-minute business assessment
      </div>
      <h4 className="mt-3 font-head text-xl text-[#1e3d3a]">Pick a time that works.</h4>
      <p className="mt-1 text-sm text-[#1e3d3a]/60">Times shown in your selected time zone. A one-time, non-refundable $250 assessment fee reserves your meeting.</p>

      {/* Month grid */}
      <div className="mt-6">
        <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-[#1e3d3a]/10 bg-[#eae7de]/50 px-3 py-2">
          <label className="flex items-center gap-2 flex-1 min-w-0">
            <Globe className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 shrink-0">Time zone</span>
            <select
              data-testid="booking-tz-select"
              value={tz}
              onChange={(e) => setTz(e.target.value)}
              className="flex-1 min-w-0 bg-transparent text-xs text-[#1e3d3a] focus:outline-none"
            >
              {TIMEZONES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
        </div>
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            data-testid="booking-prev-month"
            onClick={() => shiftMonth(-1)}
            disabled={!canPrev}
            aria-label="Previous month"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div data-testid="booking-month-title" className="font-head text-lg text-[#1e3d3a] tracking-tight">
            {monthTitle}
          </div>
          <button
            type="button"
            data-testid="booking-next-month"
            onClick={() => shiftMonth(1)}
            disabled={!canNext}
            aria-label="Next month"
            className="h-8 w-8 flex items-center justify-center rounded-full border border-[#1e3d3a]/15 text-[#1e3d3a] hover:border-[#c9a961] hover:text-[#a3823f] transition-colors duration-300 disabled:opacity-30 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-[10px] uppercase tracking-[0.2em] text-[#1e3d3a]/50 mb-1">
          {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {loading && (
            <div className="col-span-7 flex items-center justify-center py-6">
              <Loader2 className="h-4 w-4 animate-spin text-[#c9a961]" />
            </div>
          )}
          {!loading && monthCells.map((c) => {
            const info = daysMap.get(c.key);
            const hasSlots = !!info;
            const allTaken = hasSlots && info.slots.every((s) => s.taken);
            const openCount = hasSlots ? info.slots.filter((s) => !s.taken).length : 0;
            const isActive = c.key === selectedDayKey;
            const disabled = !hasSlots || allTaken;
            return (
              <button
                key={c.key}
                type="button"
                data-testid={`booking-day-${c.key}`}
                onClick={() => {
                  if (disabled) return;
                  setSelectedDayKey(c.key);
                  setSelectedSlot(null);
                }}
                disabled={disabled}
                title={openCount ? `${openCount} slot${openCount === 1 ? "" : "s"} open` : undefined}
                className={`relative h-11 rounded-lg text-sm font-head tabular-nums transition-colors duration-300 border ${
                  isActive
                    ? "border-[#c9a961] bg-[#c9a961]/15 text-[#1e3d3a] font-semibold"
                    : hasSlots && !allTaken
                    ? "border-[#1e3d3a]/12 text-[#1e3d3a] hover:border-[#c9a961]/60 hover:bg-[#c9a961]/6"
                    : "border-transparent text-[#1e3d3a]/25 cursor-not-allowed"
                } ${!c.inMonth ? "opacity-40" : ""}`}
              >
                {c.dayNum}
                {openCount > 0 && (
                  <span
                    data-testid={`booking-day-dot-${c.key}`}
                    className={`absolute left-1/2 -translate-x-1/2 bottom-1.5 h-1 w-1 rounded-full ${isActive ? "bg-[#1e3d3a]" : "bg-[#c9a961]"}`}
                    aria-hidden="true"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time chips */}
      {activeDay && (
        <div className="mt-5 flex flex-wrap gap-2">
          {activeDay.slots.map((s) => {
            const active = selectedSlot?.slot_iso === s.slot_iso;
            return (
              <button
                key={s.slot_iso}
                data-testid={`booking-slot-${s.slot_iso}`}
                onClick={() => !s.taken && setSelectedSlot(s)}
                disabled={s.taken}
                className={`rounded-lg border px-4 py-2 text-sm transition-colors duration-300 flex items-center gap-1.5 ${
                  s.taken ? "border-[#1e3d3a]/10 text-[#1e3d3a]/30 line-through cursor-not-allowed" :
                  active ? "border-[#c9a961] bg-[#c9a961] text-[#1e3d3a] font-semibold" :
                  "border-[#1e3d3a]/15 text-[#1e3d3a]/80 hover:border-[#c9a961]/50 hover:text-[#1e3d3a]"
                }`}
              >
                <Clock className="h-3 w-3" /> {timeLabel(s.slot_iso, tz)}
              </button>
            );
          })}
        </div>
      )}

      {/* Form */}
      <AnimatePresence>
        {selectedSlot && (
          <motion.form
            data-testid="booking-form"
            onSubmit={submit}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="overflow-hidden mt-6 space-y-4"
          >
            <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-4 py-3 text-sm text-[#1e3d3a]">
              Booking <strong>{new Date(selectedSlot.slot_iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</strong>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="booking-name" placeholder="Name *" className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="booking-email" placeholder="Email *" type="email" className={inputCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <input data-testid="booking-company" placeholder="Company" className={inputCls} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            <textarea data-testid="booking-goal" placeholder="What would make this call a win?" className={`${inputCls} min-h-[80px] resize-none`} value={form.goal} onChange={(e) => setForm({ ...form, goal: e.target.value })} />
            <button
              data-testid="booking-submit"
              type="submit"
              disabled={submitting}
              className="w-full rounded-full bg-[#1e3d3a] text-white py-3.5 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              Reserve my meeting <ArrowRight className="h-4 w-4" />
            </button>
          </motion.form>
        )}
      </AnimatePresence>

      {/* $250 assessment fee modal */}
      <AnimatePresence>
        {showFee && (
          <motion.div
            data-testid="assessment-fee-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-[#0f2624]/70 backdrop-blur-sm"
              onClick={() => !submitting && setShowFee(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-md rounded-3xl border border-[#c9a961]/40 bg-[#f4f1e8] p-8 shadow-2xl"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-[#a3823f] font-medium">
                <CreditCard className="h-3.5 w-3.5" /> One-time assessment fee
              </div>
              <h4 className="mt-3 font-head text-2xl text-[#1e3d3a]">Reserve your meeting</h4>
              <div className="mt-4 flex items-baseline gap-2">
                <span className="font-head text-5xl font-light text-[#1e3d3a]">$250</span>
                <span className="text-sm text-[#1e3d3a]/55">one-time · non-refundable</span>
              </div>
              <p className="mt-4 text-sm leading-relaxed text-[#1e3d3a]/70">
                A one-time, non-refundable <strong>$250 Business Assessment fee</strong> is required to
                reserve your meeting. Once payment is complete, your time is confirmed and you&apos;ll
                receive a calendar invite with your video link.
              </p>
              {selectedSlot && (
                <div className="mt-5 rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/10 px-4 py-3 text-sm text-[#1e3d3a]">
                  {new Date(selectedSlot.slot_iso).toLocaleString(undefined, { weekday: "long", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  data-testid="fee-cancel"
                  onClick={() => setShowFee(false)}
                  disabled={submitting}
                  className="flex-1 rounded-full border border-[#1e3d3a]/20 py-3 text-sm text-[#1e3d3a]/75 hover:text-[#1e3d3a] hover:border-[#1e3d3a]/50 transition-colors duration-300 disabled:opacity-50"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid="fee-pay"
                  onClick={payWithFee}
                  disabled={submitting}
                  className="flex-[2] rounded-full bg-[#1e3d3a] text-white py-3 text-sm font-semibold hover:bg-[#c9a961] hover:text-[#1e3d3a] transition-colors duration-300 disabled:opacity-60 flex items-center justify-center gap-2"
                >
                  {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Redirecting…</> : <>Pay $250 &amp; book <ArrowRight className="h-4 w-4" /></>}
                </button>
              </div>
              <p className="mt-4 text-center text-[11px] text-[#1e3d3a]/45">Secure payment via Stripe</p>
              {isAdmin && (
                <div className="mt-4 border-t border-[#1e3d3a]/10 pt-4">
                  <button
                    type="button"
                    data-testid="fee-simulate"
                    onClick={simulate}
                    disabled={submitting}
                    className="w-full rounded-full border border-dashed border-[#1e3d3a]/30 py-2.5 text-xs uppercase tracking-[0.18em] text-[#1e3d3a]/60 hover:text-[#1e3d3a] hover:border-[#1e3d3a]/60 transition-colors duration-300 disabled:opacity-50"
                  >
                    Admin: simulate payment (no charge)
                  </button>
                  <p className="mt-2 text-center text-[10px] text-[#1e3d3a]/40">Confirms the booking end-to-end without charging a card.</p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
